import React, { useState, useEffect, useRef } from 'react';
import { createApi } from '../services/apiClient';
import { getErrorMessage } from '../utils/errors';
import JsonYamlInput from './JsonYamlInput';
import RequestFieldForm from './RequestFieldForm';
import ResponseFieldForm from './ResponseFieldForm';
import { sanitizeApiName } from '../utils/validation';
import logger from '../utils/logger';

export default function ApiForm({ tenantId, collectionId, initialData, onSubmit, onCancel, isEditing, isLoading: propIsLoading }) { // Added isEditing and renamed isLoading prop
  // Initialize state with default empty strings for controlled inputs
  const [api, setApi] = useState({
      name: '',
      description: '',
      endpoint: '',
      method: 'POST', // Default method
      authHeaders: {},
      requestFields: [],
      responseFields: []
  });
  const [authHeadersText, setAuthHeadersText] = useState('{}');
  const [error, setError] = useState(null);
  // Use the isLoading prop passed down
  const isLoading = propIsLoading;
  const [useJsonYaml, setUseJsonYaml] = useState(false);
  const [urlParamsEnabled, setUrlParamsEnabled] = useState(false); // Initialize separately, useEffect will update

  const methodsWithRequestBody = ['GET', 'POST', 'PUT', 'PATCH'];
  const showRequestFields = methodsWithRequestBody.includes(api.method);

  // Effect to populate form when initialData changes (for editing)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      // Map backend fields to form state, preferring camelCase if available
      setApi({
        name: initialData.name ?? initialData.Name ?? '',
        description: initialData.description ?? initialData.Description ?? '',
        endpoint: initialData.endpoint ?? initialData.Endpoint ?? '',
        method: initialData.http_method ?? initialData.HttpMethod ?? 'POST',
        authHeaders: initialData.auth_headers ?? initialData.AuthHeaders ?? {},
        requestFields: initialData.request_config?.fields ?? initialData.Request?.fields ?? [],
        responseFields: initialData.response_schema?.fields ?? initialData.Response?.fields ?? []
      });

      const initialAuthHeaders = initialData.auth_headers ?? initialData.AuthHeaders ?? {};
      setAuthHeadersText(JSON.stringify(initialAuthHeaders, null, 2));

      const initialUrlParamsEnabled = initialData.request_config?.url_params ?? initialData.Request?.url_params ?? false;
      setUrlParamsEnabled(initialUrlParamsEnabled);
    } else {
      // Reset form if initialData is cleared or not provided
      setApi({
        name: '', description: '', endpoint: '', method: 'POST',
        authHeaders: {}, requestFields: [], responseFields: []
      });
      setAuthHeadersText('{}');
      setUrlParamsEnabled(false);
    }
  }, [initialData]); // Rerun when initialData object changes

  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    if (name === 'name') {
      const sanitizedName = sanitizeApiName(value);
      if (sanitizedName !== value) {
        setError('API name can only contain letters, numbers, underscores, and hyphens');
      } else {
        setError(null);
      }
      setApi(prev => ({
        ...prev,
        [name]: sanitizedName
      }));
    } else {
      setApi(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear previous errors

    // --- Validation ---
    if (!api.name?.trim()) {
        setError('API Name is required.');
        return;
    }
    if (!api.name.match(/^[a-zA-Z0-9_-]+$/)) {
      setError('API name can only contain letters, numbers, underscores, and hyphens.');
      return;
    }
    if (!api.endpoint?.trim()) {
        setError('Endpoint URL is required.');
        return;
    }
    // Basic URL format check (optional, can be more robust)
    try {
        new URL(api.endpoint.trim());
    } catch (_) {
        setError('Endpoint URL must be a valid URL (e.g., https://example.com/api).');
        return;
    }
    // Add more validation as needed (e.g., for auth headers JSON)
    // --- End Validation ---

    // setIsLoading is handled by the parent modal now

    const currentRequestField = requestFormRef.current?.getCurrentField();
    const currentResponseField = responseFormRef.current?.getCurrentField();

    const apiData = {
      ...api,
      // ID is handled in AddDataSourceModal payload creation
      http_method: api.method,
      // Pass fields directly, AddDataSourceModal constructs final payload
      requestFields: [
        ...api.requestFields,
        ...(currentRequestField?.name ? [currentRequestField] : [])
      ],
      urlParamsEnabled: urlParamsEnabled,
      responseFields: [
        ...api.responseFields,
        ...(currentResponseField?.name ? [currentResponseField] : [])
      ]
    };

    try {
      await onSubmit(apiData);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      // setIsLoading is handled by the parent modal now
    }
  };

  const handleAddRequestField = (field) => {
    setApi(prev => {
      const newApi = {
        ...prev,
        requestFields: [...(prev.requestFields || []), field]
      };
      return newApi;
    });
  };

  const handleAddResponseField = (field) => {
    setApi(prev => {
      const newApi = {
        ...prev,
        responseFields: [...(prev.responseFields || []), field]
      };
      return newApi;
    });
  };

  const requestFormRef = useRef();
  const responseFormRef = useRef();

  const handleAuthHeadersChange = (e) => {
    setAuthHeadersText(e.target.value);
  };

  const handleAuthHeadersBlur = () => {
    try {
      const parsed = JSON.parse(authHeadersText);
      setApi(prev => ({
        ...prev,
        authHeaders: parsed
      }));
      setAuthHeadersText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (err) {
      setError('Invalid JSON in Auth Headers');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Removed Collection Select Dropdown */}


      {useJsonYaml ? (
        <JsonYamlInput
          value={api}
          onChange={(newValue) => setApi(newValue)}
        />
      ) : (
        <>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                API Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={api.name || ''} // Ensure value is always a string
                onChange={handleInputChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md appearance-none bg-white shadow-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={api.description || ''} // Ensure value is always a string
                onChange={handleInputChange}
                rows={3}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md appearance-none bg-white shadow-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="endpoint" className="block text-sm font-medium text-gray-700">
                  Endpoint URL
                </label>
                <input
                  type="url"
                  id="endpoint"
                  name="endpoint"
                  value={api.endpoint || ''} // Ensure value is always a string
                  onChange={handleInputChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md appearance-none bg-white shadow-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="method" className="block text-sm font-medium text-gray-700">
                  HTTP Method
                </label>
                <select
                  id="method"
                  name="method"
                  value={api.method}
                  onChange={handleInputChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md appearance-none bg-white shadow-sm"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="authHeaders" className="block text-sm font-medium text-gray-700">
                Auth Headers (JSON) - Optional
              </label>
              <textarea
                id="authHeaders"
                name="authHeaders"
                value={authHeadersText}
                onChange={handleAuthHeadersChange}
                onBlur={handleAuthHeadersBlur}
                rows={3}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md appearance-none bg-white shadow-sm"
                placeholder='{"Authorization": "Bearer token", "X-API-Key": "key"}'
              />
            </div>
          </div>

          {showRequestFields && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Query Params or Request Fields</h3>
              <div className="bg-gray-50 p-4 rounded-sm mb-4">
                <RequestFieldForm
                  ref={requestFormRef}
                  onAdd={handleAddRequestField}
                  method={api.method}
                  urlParamsEnabled={urlParamsEnabled}
                  onUrlParamsChange={setUrlParamsEnabled}
                />
              </div>
              {api.requestFields.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Added Request Fields:</h4>
                  <ul className="space-y-2">
                    {api.requestFields.map((field, index) => (
                      <li key={index} className="bg-white p-3 rounded-sm border">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{field.name}</p>
                            <p className="text-sm text-gray-500">Type: {field.type}</p>
                            {field.description && (
                              <p className="text-sm text-gray-500">Description: {field.description}</p>
                            )}
                            {field.required && (
                              <p className="text-sm text-red-500">Required</p>
                            )}
                          </div>
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-800"
                            onClick={() => {
                              setApi(prev => ({
                                ...prev,
                                requestFields: prev.requestFields.filter((_, i) => i !== index)
                              }));
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Response Fields</h3>
            <div className="bg-gray-50 p-4 rounded-sm mb-4">
              <ResponseFieldForm 
                ref={responseFormRef}
                onAdd={handleAddResponseField} 
              />
            </div>
            {api.responseFields && api.responseFields.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Added Response Fields:</h4>
                <ul className="space-y-2">
                  {api.responseFields.map((field, index) => (
                    <li key={index} className="bg-white p-3 rounded-sm border">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{field.name}</p>
                          <p className="text-sm text-gray-500">Type: {field.type}</p>
                          {field.description && (
                            <p className="text-sm text-gray-500">Description: {field.description}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => {
                            setApi(prev => ({
                              ...prev,
                              responseFields: prev.responseFields.filter((_, i) => i !== index)
                            }));
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}

      <div className="flex justify-end space-x-2">
        <button 
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button 
          type="submit"
          className={`btn-primary ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading
            ? (isEditing ? 'Updating API...' : 'Creating API...')
            : (isEditing ? 'Update API' : 'Create API')}
        </button>
      </div>
    </form>
  );
}
